import { NotificationData } from '../../../services/notification.service';
import { WayNormalizedPurchase } from '../models/way-purchase.models';

export const WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE =
  'Way subscriptions can be redeemed once in 24 hours.';

export interface WayLookupNotificationContext {
  locationId: string;
  plate?: string;
}

export function isRedeemableWayPurchase(
  purchase: WayNormalizedPurchase | null | undefined
): boolean {
  if (!purchase) {
    return false;
  }
  // can_redeem is the source of truth; redeemed_today alone must not block redeem.
  if (purchase.can_redeem === false) {
    return false;
  }
  const status = String(purchase.status ?? '')
    .trim()
    .toLowerCase();
  // Missing status is treated as usable; only an explicit non-active status blocks redeem.
  return !status || status === 'active';
}

export function getActiveWayPurchases(purchases: WayNormalizedPurchase[]): WayNormalizedPurchase[] {
  return (purchases ?? []).filter(purchase => isRedeemableWayPurchase(purchase));
}

export function getUnredeemableWayPurchases(
  purchases: WayNormalizedPurchase[]
): WayNormalizedPurchase[] {
  return (purchases ?? []).filter(purchase => !isRedeemableWayPurchase(purchase));
}

function resolveUnredeemableMessage(purchase: WayNormalizedPurchase): string {
  const reason = String(purchase.unredeemable_reason ?? '').trim();
  if (reason) {
    return reason;
  }
  const status = String(purchase.status ?? '').trim();
  return status || 'Way purchase is not redeemable';
}

/**
 * Maps Way lookup purchases to a Way purchase modal notification (Way-only; no shop cards).
 * Mirrors W-only branches of order-api `decideWayLprOutcome`.
 */
export function buildNotificationFromWayLookupResponse(
  purchases: WayNormalizedPurchase[],
  context: WayLookupNotificationContext
): NotificationData | null {
  const rows = purchases ?? [];
  const redeemableWay = getActiveWayPurchases(rows);
  const unredeemableWay = getUnredeemableWayPurchases(rows);
  const rCount = redeemableWay.length;
  const uCount = unredeemableWay.length;

  const baseData: Record<string, any> = {
    location_id: context.locationId,
    source: 'order_request_lookup',
  };
  if (context.plate) {
    baseData['plate'] = context.plate;
    baseData['plateNumber'] = context.plate;
  }

  if (rCount >= 2 || (rCount >= 1 && uCount >= 1) || (rCount === 0 && uCount >= 2)) {
    return {
      type: 'way-purchase-selection',
      message: 'Select purchase to redeem',
      timestamp: new Date(),
      data: {
        ...baseData,
        purchase_options: [...redeemableWay, ...unredeemableWay].map(purchase => ({
          source: 'way' as const,
          purchase,
        })),
      },
    };
  }

  if (rCount === 1 && uCount === 0) {
    const [purchase] = redeemableWay;
    const isMembership =
      String(purchase.purchase_type ?? '')
        .trim()
        .toLowerCase() === 'membership';
    const type = isMembership ? 'way-active-subscription' : 'way-single-wash';
    return {
      type,
      message: isMembership ? 'Active subscription' : 'Single wash',
      timestamp: new Date(),
      data: {
        ...baseData,
        purchase,
      },
    };
  }

  if (rCount === 0 && uCount === 1) {
    const [purchase] = unredeemableWay;
    const isAlreadyRedeemedToday =
      String(purchase.purchase_type ?? '')
        .trim()
        .toLowerCase() === 'membership' &&
      purchase.can_redeem === false &&
      purchase.redeemed_today === true;
    const reason = isAlreadyRedeemedToday
      ? WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE
      : resolveUnredeemableMessage(purchase);
    return {
      type: isAlreadyRedeemedToday
        ? 'way-subscription-already-redeemed-today'
        : 'way-purchase-unredeemable',
      message: reason,
      timestamp: new Date(),
      data: {
        ...baseData,
        purchase,
        reason,
      },
    };
  }

  return null;
}

export function extractWayPurchaseSignature(
  notification: NotificationData | null | undefined
): string {
  if (!notification) {
    return '';
  }
  const type = String(notification.type ?? '').trim();
  if (!notification.data) {
    return type;
  }
  const data = notification.data as Record<string, any>;
  const purchase = data['purchase'] as WayNormalizedPurchase | undefined;
  if (purchase?.way_pass_id) {
    const passId = String(purchase.way_pass_id).trim();
    const status = String(purchase.status ?? data['reason'] ?? '')
      .trim()
      .toLowerCase();
    const canRedeem = purchase.can_redeem === false ? '0' : '1';
    return `${type}:${passId}:${status}:${canRedeem}`;
  }
  const options = data['purchase_options'] as
    { source?: string; purchase?: WayNormalizedPurchase }[] | undefined;
  if (!options?.length) {
    return type;
  }
  const optionPart = options
    .filter(option => option?.source === 'way' && option.purchase?.way_pass_id)
    .map(option => {
      const passId = String(option.purchase!.way_pass_id).trim();
      const status = String(option.purchase?.status ?? '')
        .trim()
        .toLowerCase();
      const canRedeem = option.purchase?.can_redeem === false ? '0' : '1';
      return `${passId}:${status}:${canRedeem}`;
    })
    .sort()
    .join('|');
  return `${type}:${optionPart}`;
}
